import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './util';

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  /** Small counter / badge after the label. */
  badge?: ReactNode;
}

export interface TabsProps {
  items: readonly TabItem[];
  /** Controlled selected tab id. */
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  label: string;
  className?: string;
  panelClassName?: string;
}

/**
 * Comic tabs: sticker-like tab buttons above an ink panel; the active tab is yellow, lifted and
 * merged into the panel. Arrow keys / Home / End move between tabs (automatic activation).
 */
export function Tabs({
  items,
  value,
  defaultValue,
  onChange,
  label,
  className,
  panelClassName,
}: TabsProps) {
  const base = useId();
  const [inner, setInner] = useState(defaultValue ?? items[0]?.id ?? '');
  const current = value ?? inner;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const select = (id: string) => {
    if (value === undefined) setInner(id);
    onChange?.(id);
    refs.current[id]?.focus();
  };
  const idx = Math.max(
    0,
    items.findIndex((t) => t.id === current),
  );

  const onKeyDown = (e: KeyboardEvent) => {
    const n = items.length;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (idx + 1) % n;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next !== null) {
      e.preventDefault();
      select(items[next]!.id);
    }
  };

  const active = items[idx];
  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        // Phones: one row that scrolls sideways (wrapped rows break the tab/panel merge). The 3px
        // bottom padding keeps the active tab's overlap inside the scroller so it is not clipped.
        className="relative z-10 -mb-[3px] flex items-end gap-0.5 overflow-x-auto pb-[3px] pl-1 sm:mb-0 sm:flex-wrap sm:gap-1.5 sm:overflow-visible sm:pb-0 sm:pl-2"
      >
        {items.map((t) => {
          const selected = t.id === active?.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                refs.current[t.id] = el;
              }}
              id={`${base}-tab-${t.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${base}-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(t.id)}
              className={cn(
                'comic -mb-[3px] inline-flex shrink-0 items-center gap-1.5 border-3 border-ink px-2 text-[16px] transition-[background-color,padding] duration-100 sm:px-3.5 sm:text-[19px]',
                selected
                  ? 'min-h-11 border-b-yellow bg-yellow pt-2 pb-2 sm:min-h-0'
                  : 'min-h-10 bg-white pt-1.5 pb-1 hover:bg-yellow-soft sm:min-h-0',
              )}
            >
              <span className="pt-[0.06em]">{t.label}</span>
              {t.badge != null && (
                <span className="border-2 border-ink bg-white px-1 text-[12px] leading-tight font-black">
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          role="tabpanel"
          id={`${base}-panel-${active.id}`}
          aria-labelledby={`${base}-tab-${active.id}`}
          tabIndex={0}
          className={cn(
            'border-3 border-ink bg-white p-3 shadow-ink-sm [border-top-color:var(--color-ink)]',
            panelClassName,
          )}
        >
          <div className="-mx-3 -mt-3 mb-3 h-1.5 border-b-3 border-ink bg-yellow" />
          {active.content}
        </div>
      )}
    </div>
  );
}
